import { Form, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { InputDatePicker } from "../../../../components/InputDatePicker";
import AppMultiSelect from "../../../../components/AppInput/AppMultiSelect";
import { ImprovementPlanFormDto, ImprovementPlanFormSelectorsDto, ImprovementPlanObjectiveDto } from "../../types";
import { useDispatch, useSelector } from "react-redux";
import { addObjective, removeObjective, setError, updateObjectiveDescription } from "../../../../redux/improvementPlanSlice";
import { RootState } from "../../../../redux/store";
import AppCommentsList from "../../../../components/AppCommentsList";
import { ImprovementPlanStatusEnum, ObjectiveFinalStatusEnum } from "../../types/enums";
import "bootstrap/dist/css/bootstrap.min.css";
import CurrencyInput from "../../../../components/AppInput/CurrencyInput";

type ImprovementPlanDetailsTabProps = {
    selectors: ImprovementPlanFormSelectorsDto;
    updateField: (field: keyof ImprovementPlanFormDto, value: ImprovementPlanFormDto[keyof ImprovementPlanFormDto]) => void;
    parseNumberOrUndefined: (value: string) => number | undefined;
};

export default function ImprovementPlanDetailsTab({
    selectors,
    updateField,
    parseNumberOrUndefined,
}: ImprovementPlanDetailsTabProps) {
    const { t } = useTranslation(["improvementPlan", "common"]);
    const dispatch = useDispatch();
    
    const improvementPlanForm = useSelector((state: RootState) => state.improvementPlanForm.data);
    const errors = useSelector((state: RootState) => state.improvementPlanForm.errors);

    return (
        <Form>
            <Row className="mt-1">
                <Col md={3}>
                    <Form.Group>
                        <Form.Label>{t("common:form.fields.id")} :</Form.Label>
                        <Form.Control
                            type="number"
                            value={improvementPlanForm.id ?? ""}
                            disabled
                        />
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group>
                        <InputDatePicker
                            value={improvementPlanForm.creationDate ? new Date(improvementPlanForm.creationDate) : null}
                            disabled={true}
                            label={t("improvementPlan:form.fields.creationDate")}
                        />
                    </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                    <Form.Group>
                        <Form.Label>{t("common:form.fields.responsible")} :</Form.Label>
                        <Form.Select
                            value={improvementPlanForm.responsibleId || ""}
                            isInvalid={!!errors.responsibleId}
                            onChange={(e) => updateField("responsibleId", Number(e.target.value) || 0)}
                        >
                            <option value="">{t("common:selectDefaultText")}</option>
                            {(selectors.responsibleOptions ?? []).map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.responsibleId}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                    <Form.Group>
                        <Form.Label>{t("common:form.fields.status")} * :</Form.Label>
                        <Form.Select
                            value={improvementPlanForm.status || ""}
                            isInvalid={!!errors.status}
                            onChange={(e) => updateField("status", Number(e.target.value) || 0)}
                        >
                            <option value="">{t("common:selectDefaultText")}</option>
                            {(Object.values(ImprovementPlanStatusEnum)
                                .filter((option) => typeof option === "number") as number[])
                                .map((value) => (
                                    <option key={value} value={value}>
                                        {t(`improvementPlan:form.status.${ImprovementPlanStatusEnum[value]}`)}
                                    </option>
                                )
                            )}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.status}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mt-1">
                <Col xs={12}>
                    <Form.Group>
                        <Form.Label>{t("common:form.fields.title")} * :</Form.Label>
                        <Form.Control
                            type="text"
                            value={improvementPlanForm.title}
                            isInvalid={!!errors.title}
                            onChange={(e) => updateField("title", e.target.value)}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.title}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            
            <Row className="mt-1">
                <Col xs={12} md={12}>
                    <Form.Group>
                        <Form.Label>{t("common:form.fields.description")} :</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={improvementPlanForm.description ?? ""}
                            onChange={(e) => updateField("description", e.target.value)}
                        />
                    </Form.Group>
                </Col>
            </Row>

            <AppCommentsList
                label={t("improvementPlan:form.fields.objectives")}
                placeholder={t("improvementPlan:form.fields.objectiveDescription")}
                items={(improvementPlanForm.objectives ?? []).map((objective: ImprovementPlanObjectiveDto) => ({
                    id: objective.id,
                    value: objective.description,
                    option: objective.finalStatus
                }))}
                getItemError={(index) => errors[`objectives[${index}].description`] as string | undefined}
                onAdd={() => dispatch(addObjective())}
                onRemove={(index) => dispatch(removeObjective(index))}
                
                onChange={(index, item) => {
                    dispatch(updateObjectiveDescription({
                        index,
                        description: item.value,
                        finalStatus: item.option
                    }));
                    dispatch(setError({ field: `objectives[${index}].description`, message: undefined }));
                }}

                addAriaLabel={t("improvementPlan:form.addObjective")}
                removeAriaLabel={t("improvementPlan:form.removeObjective")}
                options={Object.values(ObjectiveFinalStatusEnum)
                    .filter((v) => typeof v === "number")
                    .map((v) => ({
                        value: v,
                        label: t(`improvementPlan:modal.finalize.status.${ObjectiveFinalStatusEnum[v]}`)
                    }))}
            />

            <Row className="mt-4">
                <Col xs={12}>
                    <h5 className="mb-0">{t("improvementPlan:form.operativeDataTitle")}</h5>
                </Col>
            </Row>
            <Row>
                <Col xs={12} md={4}>
                    <Form.Group>
                        <InputDatePicker
                            value={improvementPlanForm.startDate ? new Date(improvementPlanForm.startDate) : null}
                            onChange={(date: Date | null) => updateField("startDate", date ?? undefined)}
                            label={`${t("common:form.fields.startDate")} :`}
                            hasError={!!errors.startDate}
                            errorMessage={errors.startDate}
                        />
                    </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                    <Form.Group>
                        <InputDatePicker
                            value={improvementPlanForm.expectedEndDate ? new Date(improvementPlanForm.expectedEndDate) : null}
                            onChange={(date: Date | null) => updateField("expectedEndDate", date ?? undefined)}
                            label={`${t("improvementPlan:actionForm.fields.expectedEndDate")} :`}
                            hasError={!!errors.expectedEndDate}
                            errorMessage={errors.expectedEndDate}
                        />
                    </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                    <Form.Group>
                        <InputDatePicker
                            value={improvementPlanForm.endDate ? new Date(improvementPlanForm.endDate) : null}
                            onChange={(date: Date | null) => updateField("endDate", date ?? undefined)}
                            label={`${t("common:form.fields.endDate")} :`}
                            hasError={!!errors.endDate}
                            errorMessage={errors.endDate}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mt-1">
                <Col xs={6}>
                    <Form.Group>
                        <Form.Label>{t("improvementPlan:form.fields.origin")} :</Form.Label>
                        <Form.Select
                            value={improvementPlanForm.originId ?? ""}
                            onChange={(e) => updateField("originId", parseNumberOrUndefined(e.target.value))}
                        >
                            <option value="">{t("common:selectDefaultText")}</option>
                            {(selectors.originOptions ?? []).map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col xs={6}>
                    <Form.Group>
                        <Form.Label>{t("improvementPlan:form.fields.type")} :</Form.Label>
                        <Form.Select
                            value={improvementPlanForm.typeId ?? ""}
                            onChange={(e) => updateField("typeId", parseNumberOrUndefined(e.target.value))}
                        >
                            <option value="">{t("common:selectDefaultText")}</option>
                            {(selectors.typeOptions ?? []).map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mt-1">
                <Col xs={6}>
                    <Form.Group>
                        <Form.Label>{t("improvementPlan:form.fields.functionalAreas")} :</Form.Label>
                        <AppMultiSelect
                            options={selectors.functionalAreaOptions ?? []}
                            selectedValues={improvementPlanForm.selectedFunctionalAreas}
                            onChange={(values) => updateField("selectedFunctionalAreas", values)}
                            isInvalid={!!errors.selectedFunctionalAreas}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.selectedFunctionalAreas}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col xs={6}>
                    <Form.Group>
                        <Form.Label>{t("improvementPlan:form.fields.services")} :</Form.Label>
                        <AppMultiSelect
                            options={selectors.serviceOptions ?? []}
                            selectedValues={improvementPlanForm.selectedServices}
                            onChange={(values) => updateField("selectedServices", values)}
                            isInvalid={!!errors.selectedServices}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.selectedServices}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mt-4">
                <Col xs={12}>
                    <h5 className="mb-0">{t("improvementPlan:form.economicDataTitle")}</h5>
                </Col>
            </Row>
            <Row>
                <Col xs={12} md={6}>
                    <Form.Group>
                        <Form.Label>{t("improvementPlan:form.fields.estimatedInverstment")} :</Form.Label>
                        <CurrencyInput
                            value={improvementPlanForm.estimatedInverstment}
                            onChange={(value) =>
                                updateField("estimatedInverstment", value)
                            }
                        />
                    </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                    <Form.Group>
                        <Form.Label>
                            {t("improvementPlan:form.fields.returnedInverstment")} :
                        </Form.Label>
                        <CurrencyInput
                            value={improvementPlanForm.returnedInverstment}
                            onChange={(value) =>
                                updateField("returnedInverstment", value)
                            }
                        /> 
                    </Form.Group>
                </Col>
            </Row>
        </Form>
    );
}
